import type { SupplierCertificateType } from '@tandemone/types'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { extractErrorMessage } from '@/api/extract-error'
import { Button } from '@/components/ui/button'
import {
  Combobox,
  ComboboxContent,
  ComboboxItem,
  ComboboxList,
  ComboboxTrigger
} from '@/components/ui/combobox'
import { FormSheet } from '@/components/ui/form-sheet'
import { Label } from '@/components/ui/label'
import { FileDropZone } from '@/domains/document'
import { captureException } from '@/domains/observability'

import { createSupplierCertificate } from './api'
import { SUPPLIER_CERTIFICATE_LABELS } from './enum-labels'

const DOC_MAX_BYTES = 50 * 1024 * 1024
const ACCEPT = 'application/pdf,image/png,image/jpeg'

/** certs offered alpha by their display label so the picker reads predictably. */
const CERT_ORDER: ReadonlyArray<SupplierCertificateType> = (
  Object.keys(SUPPLIER_CERTIFICATE_LABELS) as SupplierCertificateType[]
).sort((left, right) =>
  SUPPLIER_CERTIFICATE_LABELS[left].localeCompare(
    SUPPLIER_CERTIFICATE_LABELS[right]
  )
)

export function AddCertificateSheet() {
  const queryClient = useQueryClient()
  const [open, setOpen] = useState(false)
  const [certification, setCertification] =
    useState<SupplierCertificateType>('iso_9001')

  const createMutation = useMutation({
    mutationFn: createSupplierCertificate,
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['supplier-certificates']
      })
      toast.success('Certificate added. It will appear after review.')
      setCertification('iso_9001')
      setOpen(false)
    },
    onError: error => {
      captureException(error)
      toast.error(extractErrorMessage(error))
    }
  })

  return (
    <FormSheet
      open={open}
      onOpenChange={next => {
        if (!createMutation.isPending) setOpen(next)
      }}
      trigger={
        <Button size='hero' className='w-full sm:w-auto'>
          <Plus className='size-4' />
          Add certificate
        </Button>
      }
      title='Add a certificate'
      description='Pick the certification, then drop the certificate PDF or image. It goes live once a TandemOne reviewer verifies it.'
    >
      <div className='flex flex-col gap-1.5'>
        <Label>Certification</Label>
        <Combobox
          items={CERT_ORDER as SupplierCertificateType[]}
          value={certification}
          onValueChange={value => {
            if (value) setCertification(value as SupplierCertificateType)
          }}
          itemToStringLabel={(item: string) =>
            SUPPLIER_CERTIFICATE_LABELS[item as SupplierCertificateType]
          }
        >
          <ComboboxTrigger
            aria-label='Certification'
            className='inline-flex h-9 w-full items-center justify-between rounded-md border border-border bg-page px-3 text-sm text-ink-soft transition-colors hover:bg-cream focus-visible:ring-2 focus-visible:ring-ring/30 focus-visible:outline-none'
          >
            {SUPPLIER_CERTIFICATE_LABELS[certification]}
          </ComboboxTrigger>
          <ComboboxContent>
            <ComboboxList>
              {CERT_ORDER.map(option => (
                <ComboboxItem key={option} value={option}>
                  {SUPPLIER_CERTIFICATE_LABELS[option]}
                </ComboboxItem>
              ))}
            </ComboboxList>
          </ComboboxContent>
        </Combobox>
      </div>

      <div className='flex flex-col gap-1.5'>
        <Label>File</Label>
        <FileDropZone
          accept={ACCEPT}
          maxBytes={DOC_MAX_BYTES}
          value={null}
          onUploaded={uploaded =>
            createMutation.mutate({
              documentId: uploaded.documentId,
              certification
            })
          }
        />
        {createMutation.isPending && (
          <p className='text-[12px] text-ink-muted'>Saving…</p>
        )}
      </div>
    </FormSheet>
  )
}
